import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, parseISO, isToday, isBefore, startOfDay } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Plus, 
  Trash2, 
  Clock,
  Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WORKING_HOURS = Array.from({ length: 9 }, (_, i) => i + 9);

export default function MeetingRoomPage() {
  const { isAdmin } = useAuth();
  const { t, dateLocale } = useLanguage();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [monthBookings, setMonthBookings] = useState({});
  const [dayBookings, setDayBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(false);
  
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    title: '',
    description: '',
    start_hour: '9',
    end_hour: '10'
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchMonthBookings = useCallback(async () => {
    setLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const { data } = await axios.get(
        `${API_URL}/api/meeting-room/bookings/by-month?year=${year}&month=${month}`,
        { withCredentials: true }
      );
      setMonthBookings(data);
    } catch {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [currentMonth, t]);

  const fetchDayBookings = useCallback(async (date) => {
    setDayLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const { data } = await axios.get(
        `${API_URL}/api/meeting-room/bookings/by-date?date=${dateStr}`,
        { withCredentials: true }
      );
      setDayBookings(data);
    } catch {
      toast.error(t('error'));
    } finally {
      setDayLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonthBookings();
  }, [fetchMonthBookings]);

  useEffect(() => {
    if (selectedDate) {
      fetchDayBookings(selectedDate);
    }
  }, [selectedDate, fetchDayBookings]);

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/meeting-room/bookings`,
        {
          date: format(selectedDate, 'yyyy-MM-dd'),
          start_hour: parseInt(bookingForm.start_hour),
          end_hour: parseInt(bookingForm.end_hour),
          title: bookingForm.title,
          description: bookingForm.description
        },
        { withCredentials: true }
      );
      
      toast.success(t('meetingRoomBooked'));
      setShowBookingForm(false);
      setBookingForm({ title: '', description: '', start_hour: '9', end_hour: '10' });
      await fetchMonthBookings();
      await fetchDayBookings(selectedDate);
    } catch {
      const errorMsg = e.response?.data?.detail || t('error');
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(
        `${API_URL}/api/meeting-room/bookings/${bookingId}`,
        { withCredentials: true }
      );
      toast.success(t('bookingCancelled'));
      await fetchMonthBookings();
      if (selectedDate) {
        await fetchDayBookings(selectedDate);
      }
    } catch {
      toast.error(t('error'));
    }
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getDayStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayData = monthBookings[dateStr];
    
    if (!dayData) return 'free';
    
    if (dayData.total_hours >= 9) return 'full';
    if (dayData.total_hours > 0) return 'partial';
    
    return 'free';
  };

  const getDayClasses = (date) => {
    const status = getDayStatus(date);
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    const isPast = isBefore(date, startOfDay(new Date()));
    
    let classes = 'w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center text-xs md:text-sm font-semibold transition-all cursor-pointer ';
    
    if (!isSameMonth(date, currentMonth)) {
      classes += 'text-slate-300 ';
    } else if (isPast) {
      classes += 'text-slate-400 bg-slate-100 cursor-not-allowed ';
    } else if (isSelected) {
      classes += 'bg-blue-500 text-white ring-2 ring-blue-300 ';
    } else if (isToday(date)) {
      classes += 'ring-2 ring-blue-400 ';
    }
    
    if (!isPast && isSameMonth(date, currentMonth)) {
      if (status === 'full') {
        classes += 'bg-rose-400 text-white hover:bg-rose-500 ';
      } else if (status === 'partial') {
        classes += 'bg-amber-400 text-white hover:bg-amber-500 ';
      } else {
        classes += 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 ';
      }
    }
    
    return classes;
  };

  const getOccupiedHours = () => {
    const occupied = new Set();
    dayBookings.forEach(b => {
      for (let h = b.start_hour; h < b.end_hour; h++) {
        occupied.add(h);
      }
    });
    return occupied;
  };

  const occupiedHours = getOccupiedHours();

  const dayLetters = t('dayLetters');
  const dayShort = t('dayShort');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="meeting-room-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-2 md:px-4 py-4 md:py-8">
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">{t('meetingRoomTitle')}</h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">{t('meetingRoomSubtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
          {/* Calendar */}
          <Card className="card-floating lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2 md:pb-4">
              <CardTitle className="text-lg md:text-xl font-extrabold text-slate-900">
                {t('calendar')}
              </CardTitle>
              <div className="flex items-center gap-1 md:gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handlePrevMonth}
                  className="rounded-full h-8 w-8 md:h-10 md:w-10"
                >
                  <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
                </Button>
                <span className="text-sm md:text-lg font-bold min-w-[100px] md:min-w-[150px] text-center">
                  {format(currentMonth, 'MMMM yyyy', { locale: dateLocale })}
                </span>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleNextMonth}
                  className="rounded-full h-8 w-8 md:h-10 md:w-10"
                >
                  <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-3 md:p-6 pt-0">
              {loading ? (
                <div className="flex items-center justify-center h-48 md:h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-2 md:gap-4 justify-center text-xs md:text-sm mb-4 md:mb-6">
                    <div className="flex items-center gap-1 md:gap-2">
                      <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-emerald-100 border border-emerald-300" />
                      <span className="text-slate-600">{t('free')}</span>
                    </div>
                    <div className="flex items-center gap-1 md:gap-2">
                      <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-amber-400" />
                      <span className="text-slate-600">{t('partial')}</span>
                    </div>
                    <div className="flex items-center gap-1 md:gap-2">
                      <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-rose-400" />
                      <span className="text-slate-600">{t('fullDay')}</span>
                    </div>
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 md:mb-4">
                    {Array.isArray(dayLetters) && dayLetters.map((letter, i) => (
                      <div key={`day-${i}`} className="text-center text-xs md:text-sm font-semibold text-slate-500 py-1 md:py-2">
                        <span className="md:hidden">{letter}</span>
                        <span className="hidden md:inline">{Array.isArray(dayShort) ? dayShort[i] : letter}</span>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1 md:gap-2">
                    {Array.from({ length: (monthStart.getDay() + 6) % 7 }).map((_, i) => (
                      <div key={`empty-${i}`} className="w-8 h-8 md:w-10 md:h-10" />
                    ))}
                    
                    {calendarDays.map(date => (
                      <div
                        key={date.toISOString()}
                        className={getDayClasses(date)}
                        onClick={() => !isBefore(date, startOfDay(new Date())) && handleDateClick(date)}
                        data-testid={`calendar-day-${format(date, 'yyyy-MM-dd')}`}
                      >
                        {format(date, 'd')}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Day detail */}
          <Card className="card-floating">
            <CardHeader className="flex flex-row items-center justify-between pb-2 md:pb-4">
              <CardTitle className="text-base md:text-xl font-extrabold text-slate-900">
                {selectedDate 
                  ? format(selectedDate, 'EEE d MMM', { locale: dateLocale })
                  : t('selectDay')
                }
              </CardTitle>
              {selectedDate && !isBefore(selectedDate, startOfDay(new Date())) && (
                <Button
                  onClick={() => setShowBookingForm(true)}
                  data-testid="new-meeting-btn"
                  className="btn-primary text-xs md:text-sm"
                  size="sm"
                >
                  <Plus className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                  {t('book')}
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-3 md:p-6 pt-0">
              {!selectedDate ? (
                <div className="text-center py-6 md:py-8 text-slate-400">
                  <Users className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 opacity-50" />
                  <p className="text-sm md:text-base">{t('clickDay')}</p>
                </div>
              ) : dayLoading ? (
                <div className="flex items-center justify-center h-32 md:h-48">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="space-y-2 md:space-y-4">
                  <div className="space-y-1">
                    {WORKING_HOURS.map(hour => {
                      const isOccupied = occupiedHours.has(hour);
                      const booking = dayBookings.find(b => b.start_hour <= hour && b.end_hour > hour);
                      
                      return (
                        <div
                          key={hour}
                          className={`flex items-center gap-1 md:gap-2 p-1.5 md:p-2 rounded-lg text-xs md:text-sm ${
                            isOccupied 
                              ? booking?.is_mine 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-rose-100 text-rose-800'
                              : 'bg-slate-50 text-slate-600'
                          }`}
                        >
                          <Clock className="w-3 h-3 md:w-4 md:h-4" />
                          <span className="font-mono w-10 md:w-16">{hour}:00</span>
                          {booking && booking.start_hour === hour && (
                            <div className="flex-1 flex items-center justify-between min-w-0">
                              <div className="truncate">
                                <span className="font-semibold">{booking.title}</span>
                                <span className="text-[10px] md:text-xs ml-1 md:ml-2 hidden sm:inline">
                                  ({booking.start_hour}-{booking.end_hour})
                                </span>
                                {isAdmin && (
                                  <span className="text-[10px] md:text-xs ml-1 md:ml-2 opacity-70 hidden md:inline">
                                    - {booking.user_name}
                                  </span>
                                )}
                              </div>
                              {(booking.is_mine || isAdmin) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleCancelBooking(booking.id)}
                                  className="h-5 w-5 md:h-6 md:w-6 text-rose-500 hover:text-rose-700 flex-shrink-0"
                                  data-testid={`cancel-meeting-${booking.id}`}
                                >
                                  <Trash2 className="w-2.5 h-2.5 md:w-3 md:h-3" />
                                </Button>
                              )}
                            </div>
                          )}
                          {!isOccupied && (
                            <span className="text-slate-400 text-xs">{t('freeSlot')}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Booking Dialog */}
        <Dialog open={showBookingForm} onOpenChange={setShowBookingForm}>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-extrabold">
                {t('bookMeetingRoom')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleBookingSubmit} className="space-y-4 mt-4">
              <div>
                <p className="text-sm text-slate-500 mb-4">
                  {selectedDate && format(selectedDate, 'EEEE d MMMM yyyy', { locale: dateLocale })}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title" className="font-semibold">{t('meetingTitle')}</Label>
                <Input
                  id="title"
                  data-testid="meeting-title-input"
                  value={bookingForm.title}
                  onChange={(e) => setBookingForm({ ...bookingForm, title: e.target.value })}
                  placeholder={t('meetingTitlePlaceholder')}
                  className="rounded-xl"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-semibold">{t('startTime')}</Label>
                  <Select
                    value={bookingForm.start_hour}
                    onValueChange={(value) => setBookingForm({ ...bookingForm, start_hour: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="meeting-start-hour">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WORKING_HOURS.map(h => (
                        <SelectItem 
                          key={h} 
                          value={h.toString()}
                          disabled={occupiedHours.has(h)}
                        >
                          {h}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">{t('endTime')}</Label>
                  <Select
                    value={bookingForm.end_hour}
                    onValueChange={(value) => setBookingForm({ ...bookingForm, end_hour: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="meeting-end-hour">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WORKING_HOURS.map(h => (
                        <SelectItem 
                          key={h + 1} 
                          value={(h + 1).toString()}
                          disabled={parseInt(bookingForm.start_hour) >= h + 1}
                        >
                          {h + 1}:00
                        </SelectItem>
                      ))}
                      <SelectItem value="20">20:00</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="font-semibold">{t('description')}</Label>
                <Textarea
                  id="description"
                  data-testid="meeting-description-input"
                  value={bookingForm.description}
                  onChange={(e) => setBookingForm({ ...bookingForm, description: e.target.value })}
                  placeholder={t('descriptionPlaceholder')}
                  className="rounded-xl"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBookingForm(false)}
                  className="flex-1 rounded-full"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="confirm-meeting-btn"
                  className="flex-1 btn-primary"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('confirm')
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
